// src/pages/task/TaskStatsPage.tsx

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useTaskAnalytic, useUserprofile, useUser, useI18n, useTheme } from '@react/hooks';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  ErrorAlert,
} from '@react/components';
import type { TaskStats } from '@core/types/task';
import { taskStatusMap, taskPriorityMap } from '@/features/task/logic';
import {
  StatTile,
  BarChart,
  CATEGORICAL,
  NEUTRAL,
  PRIORITY_ORDINAL,
  TYPE_SLOT,
  STATUS_COLOR,
  pickColor,
  type BarItem,
} from './TaskStatsCharts';

type Preset = 'all' | '7d' | '30d' | 'quarter' | 'custom';

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

/** Build a readable name for a profile, falling back to username/email
 * instead of a raw id — mirrors UserPicker's getProfileLabel so an assignee
 * reads the same way here as everywhere else in the app. */
function resolveProfileLabel(profile: any, userMap: Map<string, any>): string | null {
  if (!profile) return null;
  if (profile.display_name) return profile.display_name;
  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ');
  if (fullName) return fullName;
  const userId = profile.user?.id;
  if (userId) {
    const user = userMap.get(userId);
    if (user?.username) return user.username;
    if (user?.email) return user.email;
  }
  return null;
}

/**
 * Task statistics dashboard. Chart choices follow the `/dataviz` skill (see
 * TaskStatsCharts.tsx header): status breakdown uses this app's existing
 * status semantics (open/in-progress/done/blocked), priority uses an ordinal
 * one-hue ramp (priority is a tier, not an identity), type and assignee use
 * the categorical / single-hue nominal rules respectively.
 */
export const TaskStatsPage = observer(() => {
  const store = useTaskAnalytic();
  const profileStore = useUserprofile();
  const userStore = useUser();
  const { t } = useI18n();
  const { isDark } = useTheme();

  const [stats, setStats] = useState<TaskStats | null>(null);
  const [preset, setPreset] = useState<Preset>('all');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');

  // Resolve the active window into ISO bounds sent to the server. Custom
  // uses the two date inputs directly; presets compute from "now".
  const resolvedRange = useMemo(() => {
    switch (preset) {
      case '7d':
        return { start: isoDaysAgo(7), end: undefined as string | undefined };
      case '30d':
        return { start: isoDaysAgo(30), end: undefined };
      case 'quarter':
        return { start: isoDaysAgo(90), end: undefined };
      case 'custom':
        return {
          start: periodStart ? new Date(periodStart).toISOString() : undefined,
          end: periodEnd ? new Date(periodEnd).toISOString() : undefined,
        };
      default:
        return { start: undefined, end: undefined };
    }
  }, [preset, periodStart, periodEnd]);

  useEffect(() => {
    const params: { period_start?: string; period_end?: string } = {};
    if (resolvedRange.start) params.period_start = resolvedRange.start;
    if (resolvedRange.end) params.period_end = resolvedRange.end;
    store.GetTaskStats(params)
      .then((res) => setStats(res?.stats ?? null))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedRange.start, resolvedRange.end]);

  // Load profiles/users once so by_assignee ids can resolve to display
  // names, the same fallback data source UserPicker uses elsewhere.
  useEffect(() => {
    if (profileStore.items.length === 0 && !profileStore.isLoading) {
      profileStore.ListUserprofiles({ sort: [], pagination: { limit: 100, cursor: '', direction: 'forward' } });
    }
    if (userStore.items.length === 0 && !userStore.isLoading) {
      userStore.ListUsers({ sort: [], pagination: { limit: 100, cursor: '', direction: 'forward' } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const u of userStore.items || []) {
      if (u.entity?.id) map.set(u.entity.id, u);
    }
    return map;
  }, [userStore.items]);

  const profileByUserId = useMemo(() => {
    const map = new Map<string, any>();
    for (const p of profileStore.items || []) {
      const uid = (p as any).user?.id || (p as any).entity?.id;
      if (uid) map.set(uid, p);
    }
    return map;
  }, [profileStore.items]);

  const hasActivePeriod = preset !== 'all';

  // ---------------------------------------------------------------------
  // Derived chart data
  // ---------------------------------------------------------------------

  const statusItems: BarItem[] = useMemo(() => {
    if (!stats) return [];
    const rows: { key: string; value: number; color: any }[] = [
      { key: 'TASK_STATUS_OPEN', value: stats.open ?? 0, color: STATUS_COLOR.open },
      { key: 'TASK_STATUS_IN_PROGRESS', value: stats.in_progress ?? 0, color: STATUS_COLOR.inProgress },
      { key: 'TASK_STATUS_DONE', value: stats.done ?? 0, color: STATUS_COLOR.done },
      { key: 'TASK_STATUS_BLOCKED', value: stats.blocked ?? 0, color: STATUS_COLOR.blocked },
    ];
    return rows
      .filter((r) => r.value > 0)
      .map((r) => ({
        key: r.key,
        label: t(taskStatusMap[r.key].labelKey),
        value: r.value,
        color: pickColor(r.color, isDark),
      }));
  }, [stats, isDark, t]);

  const priorityItems: BarItem[] = useMemo(() => {
    if (!stats?.by_priority) return [];
    return Object.entries(stats.by_priority)
      .filter(([, count]) => (count as number) > 0)
      .sort((a, b) => {
        const order = ['TASK_PRIORITY_LOWEST', 'TASK_PRIORITY_LOW', 'TASK_PRIORITY_MEDIUM', 'TASK_PRIORITY_HIGH', 'TASK_PRIORITY_HIGHEST'];
        return order.indexOf(a[0]) - order.indexOf(b[0]);
      })
      .map(([key, count]) => {
        const mapping = taskPriorityMap[key];
        const ramp = PRIORITY_ORDINAL[key] || NEUTRAL;
        return {
          key,
          label: mapping ? t(mapping.labelKey) : key,
          value: count as number,
          color: pickColor(ramp, isDark),
        };
      });
  }, [stats, isDark, t]);

  const typeItems: BarItem[] = useMemo(() => {
    if (!stats?.by_type) return [];
    let otherCount = 0;
    const known: BarItem[] = [];
    for (const [key, count] of Object.entries(stats.by_type)) {
      if ((count as number) <= 0) continue;
      const slot = TYPE_SLOT[key];
      if (slot) {
        known.push({
          key,
          label: t(`task.type.${key.replace('TASK_TYPE_', '').toLowerCase()}`),
          value: count as number,
          color: pickColor(slot, isDark),
        });
      } else {
        otherCount += count as number;
      }
    }
    known.sort((a, b) => b.value - a.value);
    if (otherCount > 0) {
      known.push({ key: '__other', label: t('task.stats.chart.other'), value: otherCount, color: pickColor(NEUTRAL, isDark) });
    }
    return known;
  }, [stats, isDark, t]);

  const typeLegend = useMemo(
    () => typeItems.map((it) => ({ label: it.label, color: it.color })),
    [typeItems],
  );

  const assigneeItems: BarItem[] = useMemo(() => {
    if (!stats?.by_assignee) return [];
    const color = pickColor(CATEGORICAL[0], isDark);
    return Object.entries(stats.by_assignee)
      .filter(([, count]) => (count as number) > 0)
      .map(([userId, count]) => {
        const profile = profileByUserId.get(userId);
        const resolved = resolveProfileLabel(profile, userMap);
        return {
          key: userId,
          label: resolved || `${userId.substring(0, 8)}…`,
          value: count as number,
          color: resolved ? color : pickColor(NEUTRAL, isDark),
          note: resolved ? undefined : t('task.stats.chart.unresolvedAssignee'),
        };
      })
      .sort((a, b) => b.value - a.value);
  }, [stats, profileByUserId, userMap, isDark, t]);

  const timeItems: BarItem[] = useMemo(() => {
    if (!stats) return [];
    const logged = stats.total_logged_hours ?? 0;
    const estimated = stats.total_estimated_hours ?? 0;
    if (logged <= 0 && estimated <= 0) return [];
    return [
      { key: 'logged', label: t('task.stats.chart.logged'), value: Math.round(logged * 10) / 10, color: pickColor(CATEGORICAL[0], isDark) },
      { key: 'estimated', label: t('task.stats.chart.estimated'), value: Math.round(estimated * 10) / 10, color: pickColor(CATEGORICAL[1], isDark) },
    ];
  }, [stats, isDark, t]);

  const completionPct = stats && stats.total > 0 ? Math.round(((stats.done ?? 0) / stats.total) * 100) : 0;

  const presetOptions: { key: Preset; labelKey: string }[] = [
    { key: 'all', labelKey: 'task.stats.period.allTime' },
    { key: '7d', labelKey: 'task.stats.period.last7' },
    { key: '30d', labelKey: 'task.stats.period.last30' },
    { key: 'quarter', labelKey: 'task.stats.period.quarter' },
    { key: 'custom', labelKey: 'task.stats.period.custom' },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <nav className="text-sm">
          <Link to="/tasks" className="text-muted-foreground hover:text-foreground">
            {t('task.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('task.statistics')}</span>
        </nav>
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('task.statistics')}</h1>
      </div>

      {/* Period filter — one row above everything it scopes */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-end gap-3">
          <div role="group" aria-label={t('task.stats.period.presetsLabel')} className="flex flex-wrap gap-1.5">
            {presetOptions.map((opt) => (
              <Button
                key={opt.key}
                type="button"
                size="sm"
                variant={preset === opt.key ? 'primary' : 'secondary'}
                onClick={() => setPreset(opt.key)}
                aria-pressed={preset === opt.key}
              >
                {t(opt.labelKey)}
              </Button>
            ))}
          </div>

          {preset === 'custom' && (
            <>
              <label className="flex flex-col text-xs text-muted-foreground">
                {t('common.from')}
                <input
                  type="date"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  className="mt-1 rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground"
                />
              </label>
              <label className="flex flex-col text-xs text-muted-foreground">
                {t('common.to')}
                <input
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  className="mt-1 rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground"
                />
              </label>
            </>
          )}
        </div>

        {/* Known framework defect (framework-giph): flat GET query params on
            this endpoint don't reach the Fiber/gorilla-schema binder unless
            they're cased as the Go struct field name, so period_start/
            period_end are effectively dropped server-side today. Disclose
            rather than silently show a filter that doesn't filter. */}
        {hasActivePeriod && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <svg className="h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
            </svg>
            {t('task.stats.period.transportNote')}
          </p>
        )}
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {store.isLoading ? (
        <StatsSkeleton />
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile label={t('task.totalTasks')} value={String(stats?.total ?? 0)} />
            <StatTile
              label={t('task.stats.kpi.completionRate')}
              value={`${completionPct}%`}
              hint={t('task.stats.kpi.completionHint', { done: String(stats?.done ?? 0), total: String(stats?.total ?? 0) })}
              tone="good"
            />
            <StatTile label={t('task.blockedTasks')} value={String(stats?.blocked ?? 0)} tone={(stats?.blocked ?? 0) > 0 ? 'critical' : 'neutral'} />
            <StatTile label={t('task.overdueTasks')} value={String(stats?.overdue ?? 0)} tone={(stats?.overdue ?? 0) > 0 ? 'critical' : 'neutral'} />
          </div>

          {/* Status breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>{t('task.stats.section.statusBreakdown')}</CardTitle>
              <CardDescription>{t('task.taskCount')}</CardDescription>
            </CardHeader>
            <CardContent>
              <BarChart
                items={statusItems}
                total={stats?.total}
                valueLabel={t('task.tasks')}
                percentLabel={(pct) => t('task.stats.chart.percentOfTotal', { pct: String(pct) })}
                emptyMessage={t('task.stats.chart.emptyStatus')}
                tableToggleLabel={t('task.stats.chart.viewAsTable')}
                columnLabel={t('task.status._')}
              />
            </CardContent>
          </Card>

          {/* By priority (ordinal ramp) */}
          <Card>
            <CardHeader>
              <CardTitle>{t('task.byPriority')}</CardTitle>
            </CardHeader>
            <CardContent>
              <BarChart
                items={priorityItems}
                total={stats?.total}
                valueLabel={t('task.tasks')}
                percentLabel={(pct) => t('task.stats.chart.percentOfTotal', { pct: String(pct) })}
                emptyMessage={t('task.stats.chart.emptyPriority')}
                tableToggleLabel={t('task.stats.chart.viewAsTable')}
                axisCaption={t('task.stats.chart.priorityCaption')}
                columnLabel={t('task.priority._')}
              />
            </CardContent>
          </Card>

          {/* By type (categorical) */}
          <Card>
            <CardHeader>
              <CardTitle>{t('task.stats.section.byType')}</CardTitle>
            </CardHeader>
            <CardContent>
              <BarChart
                items={typeItems}
                total={stats?.total}
                valueLabel={t('task.tasks')}
                percentLabel={(pct) => t('task.stats.chart.percentOfTotal', { pct: String(pct) })}
                emptyMessage={t('task.stats.chart.emptyType')}
                tableToggleLabel={t('task.stats.chart.viewAsTable')}
                legend={typeItems.length >= 2 ? typeLegend : undefined}
              />
            </CardContent>
          </Card>

          {/* By assignee (nominal, single hue, ranked) */}
          <Card>
            <CardHeader>
              <CardTitle>{t('task.byAssignee')}</CardTitle>
            </CardHeader>
            <CardContent>
              <BarChart
                items={assigneeItems}
                total={stats?.total}
                valueLabel={t('task.tasks')}
                percentLabel={(pct) => t('task.stats.chart.percentOfTotal', { pct: String(pct) })}
                emptyMessage={t('task.stats.chart.emptyAssignee')}
                tableToggleLabel={t('task.stats.chart.viewAsTable')}
                columnLabel={t('task.assignee')}
              />
            </CardContent>
          </Card>

          {/* Time: avg completion + logged vs estimated */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <StatTile
              label={t('task.avgCompletionHours')}
              value={stats?.avg_completion_hours != null ? Number(stats.avg_completion_hours).toFixed(1) : '--'}
            />
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>{t('task.stats.section.timeComparison')}</CardTitle>
              </CardHeader>
              <CardContent>
                <BarChart
                  items={timeItems}
                  valueLabel={t('task.timeTracking.hoursShort')}
                  percentLabel={(pct) => t('task.stats.chart.percentOfTotal', { pct: String(pct) })}
                  emptyMessage={t('task.stats.chart.emptyTime')}
                  tableToggleLabel={t('task.stats.chart.viewAsTable')}
                  total={(stats?.total_logged_hours ?? 0) + (stats?.total_estimated_hours ?? 0) || undefined}
                />
                {timeItems.length > 0 && (stats?.total_estimated_hours ?? 0) === 0 && (
                  <p className="mt-3 text-xs text-muted-foreground">{t('task.stats.chart.estimatedNotTracked')}</p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
});

function StatsSkeleton() {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2 animate-pulse">
              <div className="h-3 w-20 bg-muted rounded mb-2" />
              <div className="h-8 w-16 bg-muted rounded" />
            </CardHeader>
          </Card>
        ))}
      </div>
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i}>
          <CardHeader><div className="h-5 w-32 bg-muted rounded animate-pulse" /></CardHeader>
          <CardContent className="space-y-3">
            {Array.from({ length: 4 }).map((_, j) => (
              <div key={j} className="flex items-center gap-3 animate-pulse">
                <div className="h-4 w-20 bg-muted rounded" />
                <div className="flex-1 h-2.5 bg-muted rounded-full" />
                <div className="h-4 w-6 bg-muted rounded" />
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </>
  );
}

export default TaskStatsPage;
